/**
 * When any product title in a collection row wraps, give every card in that
 * row the same title height so price and reviews stay even.
 *
 * Cheap: one pass over visible cards. Runs on load, fonts, window resize,
 * filter/load-more, and theme editor section events — not on every DOM change.
 */
(function () {
  const WRAPPER = '.product-card-wrapper';
  const CLASS_NAME = 'product-card--title-wrap';
  const LINE_VAR = '--card-title-lines';
  const ROW_TOLERANCE = 8;

  function getVisibleHeading(wrapper) {
    return wrapper.querySelector(':scope > .card > .card__content > .card__information > .card__heading');
  }

  function isAlignable(wrapper) {
    const card = wrapper.querySelector(':scope > .card');
    if (!card || card.classList.contains('card--horizontal')) return false;
    if (!wrapper.getClientRects().length) return false;
    return Boolean(getVisibleHeading(wrapper));
  }

  function getTitleLineCount(wrapper) {
    const heading = getVisibleHeading(wrapper);
    if (!heading) return 1;

    const title = heading.querySelector('a') || heading;
    const styles = getComputedStyle(title);
    let lineHeight = parseFloat(styles.lineHeight);
    if (!lineHeight) {
      lineHeight = parseFloat(styles.fontSize) * 1.3;
    }

    const height = title.getBoundingClientRect().height;
    if (!height) return 1;

    return Math.max(1, Math.round(height / lineHeight));
  }

  function resetCard(wrapper) {
    wrapper.classList.remove(CLASS_NAME);
    wrapper.style.removeProperty(LINE_VAR);
  }

  function groupByRow(wrappers) {
    const rows = [];

    wrappers.forEach((wrapper) => {
      const top = wrapper.getBoundingClientRect().top;
      const row = rows.find((entry) => Math.abs(entry.top - top) <= ROW_TOLERANCE);
      if (row) {
        row.cards.push(wrapper);
        return;
      }
      rows.push({ top, cards: [wrapper] });
    });

    return rows;
  }

  function alignProductCardReviews(root) {
    const wrappers = Array.from((root || document).querySelectorAll(WRAPPER)).filter(isAlignable);

    wrappers.forEach(resetCard);
    if (!wrappers.length) return;

    groupByRow(wrappers).forEach((row) => {
      const maxLines = row.cards.reduce((max, wrapper) => Math.max(max, getTitleLineCount(wrapper)), 1);
      if (maxLines < 2) return;

      row.cards.forEach((wrapper) => {
        wrapper.style.setProperty(LINE_VAR, String(maxLines));
        wrapper.classList.add(CLASS_NAME);
      });
    });
  }

  const schedule = typeof debounce === 'function'
    ? debounce(() => alignProductCardReviews(), 150)
    : (() => {
        let timer;
        return () => {
          clearTimeout(timer);
          timer = setTimeout(() => alignProductCardReviews(), 150);
        };
      })();

  function run() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => alignProductCardReviews());
    });
  }

  window.alignProductCardReviews = alignProductCardReviews;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(run).catch(function () {});
  }

  window.addEventListener('resize', schedule);
  document.addEventListener('shopify:section:load', run);
  document.addEventListener('shopify:section:reorder', run);
})();
